<script>
import LogGrokPatternForm from '@/components/Log/LogGrokPatternForm.vue';
import LogGrokDeleteForm from '@/components/Log/LogGrokDeleteForm.vue';
import RexDropDownV3 from '@/rex-components/RexDropDownV3.vue';
import copy from '@/utils/copy';
import { notificationSucess, notificationError } from '@/utils/notification';
import { LOGService } from '@/services/api/log.service';
import {
  COPY_MENU_KEY,
  DELETE_MENU_KEY,
  EDIT_MENU_KEY,
  GROK,
  LOG_TYPE_RAW,
} from '@/components/Log/log.constants';
import { mapGetters, mapMutations } from 'vuex';
import {
  grokSystemGeneratedLabel,
  grokTableMenuItems,
} from '@/components/Log/log.types';
import { LOG_RESOURCE_GROUP } from '@/router/constant';
import { usePermissions } from '@/hooks/usePermissions';

export default {
  components: { LogGrokPatternForm, LogGrokDeleteForm, RexDropDownV3 },
  props: {
    rowDetails: {
      type: Object,
      required: true,
    },
  },
  computed: {
    initGrokValues() {
      return {
        patternName: this.rowDetails.rule_name,
        source: this.rowDetails.service_id,
        tag: this.rowDetails.tag_name,
        grokPattern: this.rowDetails?.pattern,
        isGroup: this.rowDetails?.key?.is_group,
      };
    },
    enableDelete() {
      return this.rowDetails.rule_name !== grokSystemGeneratedLabel;
    },
    hasWritePermission() {
      const { hasCapabilityWritePermission } = usePermissions();

      return hasCapabilityWritePermission(LOG_RESOURCE_GROUP);
    },
    getMenuItems() {
      return grokTableMenuItems().filter((obj) => {
        if (obj.isAdminRole)
          return obj.key === DELETE_MENU_KEY
            ? this.enableDelete && this.hasWritePermission
            : this.hasWritePermission;
        return true;
      });
    },
  },
  data() {
    return {
      openEditForm: false,
      openDeleteForm: false,
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', ['setReloadGrokTable']),
    handleCopy(content) {
      copy(content?.pattern);
      notificationSucess(this.$t('copied'));
    },
    async deleteGrok() {
      try {
        await LOGService.deleteLogRule(this.rowDetails?.id);
        this.setReloadGrokTable(true);
        notificationSucess(this.$t('log.deleteGrokPatternSuccMsg'));
        this.openDeleteForm = false;
      } catch (error) {
        console.log('error', error);
        notificationError(this.$t('log.deleteGrokPatternErrMsg'));
        this.openDeleteForm = false;
      }
    },
    async editGrok(grokDetails) {
      try {
        let ruleData = {
          rule_name: grokDetails.patternName,
          key: {
            service_id: grokDetails.source,
            is_group: grokDetails.isGroup,
          },
          parser_type: GROK,
          attributes: {
            parsing_pattern: grokDetails.grokPattern,
          },
          log_type: LOG_TYPE_RAW,
        };
        if (!grokDetails.isGroup) {
          ruleData.key.tag_name = grokDetails.tag;
        }
        const { data } = await LOGService.addLogRule(ruleData);

        if (data.success) {
          this.setReloadGrokTable(true);
          notificationSucess(this.$t('log.updateGrokPatternSuccMsg'));
        } else {
          notificationError(this.$t('log.updateGrokPatternErrMsg'));
        }
        this.openEditForm = false;
      } catch (error) {
        console.log('error');
        notificationError(this.$t('log.updateGrokPatternErrMsg'));
        this.openEditForm = false;
      }
    },
    onItemClick(item) {
      switch (item.key) {
        case DELETE_MENU_KEY:
          this.openDeleteForm = true;
          break;
        case EDIT_MENU_KEY:
          this.openEditForm = true;
          break;
        case COPY_MENU_KEY:
          this.handleCopy(this.rowDetails);
          break;
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="grok-actions-box">
      <RexDropDownV3
        :items="getMenuItems"
        @itemClick="onItemClick"
        :overlayStyle="{ width: '12rem' }"
      >
        <BaseIcon
          name="menu-icon"
          iconColor="rgb(180, 180, 180)"
          class="log-grok-table-actions-icon"
        />
      </RexDropDownV3>
    </div>
    <LogGrokPatternForm
      v-if="openEditForm"
      @onClose="openEditForm = false"
      :isEdit="true"
      :initValues="initGrokValues"
      @onSubmit="editGrok"
    />
    <LogGrokDeleteForm
      v-if="openDeleteForm"
      @onClose="openDeleteForm = false"
      @onSubmit="deleteGrok"
    />
  </div>
</template>

<style lang="scss" scoped>
.grok-actions-box {
  display: flex;
  justify-content: flex-end;
}
.grok-copy-pattern {
  cursor: pointer;
  @include svg-fill-pointer($color-brand-accent);
  transform: scale(0.7);
}
.grok-delete-icon {
  @include svg-fill-pointer($color-brand-accent);
  cursor: pointer;
  margin-left: 1.188rem;
}
.grok-edit-icon {
  @include svg-fill-pointer($color-brand-accent);
  cursor: pointer;
  margin-left: 1.188rem;
}
.log-grok-table-actions-icon {
  padding: 0rem 0.5rem;
  cursor: pointer;
}
</style>
